import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import Pagination from "../../../components/content/pagination/pagination";
import FilterForm from "../../../components/home-components/filter-form/filter-form";
import Header from "../../../components/home-components/header/header";
import Slider from "../../../components/home-components/slider";
import Table from "../../../components/home-components/table";
import TableCounter from "../../../components/home-components/table/components/counter";
import TableFilter, { FilterOption } from "../../../components/home-components/table/components/filter";
import PageSizeFilter from "../../../components/home-components/table/components/filter/page-size-filter";
import { HeaderItem } from "../../../components/home-components/table/components/header";
import {
  DataCell,
  InfoTableContainer,
  TableRow,
} from "../../../components/home-components/table/index.styled";
import {
  getCandidatePosition,
  getCandidateSalary,
  getMainSkills,
} from "../../../components/home-components/table/utils/table.utils";
import Modal from "../../../components/modal/modal";
import Talent from "../../../components/talent/talent";
import { useModal, useQuery } from "../../../hooks";
import useCandidateStatus from "../../../hooks/use-candidate-status";
import useChat from "../../../hooks/use-chat";
import { Candidate } from "../../../models/candidate.model";
import HomeDashboardPageQuery from "../../../models/home-dashboard-page-query.model";
import { OriginType } from "../../../models/track-type.enum";
import { AppState } from "../../../store/app-state";
import { clearCandidateDetail } from "../../../store/candidate/candidate-actions";
import { fetchCompanyPlan } from "../../../store/company/company-actions";
import {
  clearCurrentEventTrackData,
  fetchCandidateProcess,
  fetchEventData,
  fetchEventTracks,
  fetchTrackCandidates,
  fetchTrackSkills,
  updateCurrentEventTrackData,
} from "../../../store/event/event-actions";
import {
  fetchSetInvisible,
  fetchSetVisible,
} from "../../../store/load/load-actions";
import { getCurrentPlan } from "../../../store/plan/plan-actions";
import {
  CompanyTrackContainer,
  Container,
  ContainerTable,
  Content,
  DashboardHeader,
  FilterLabel,
  TrackTitle,
  ViewDesktopFilters,
} from "./company-track-page.styled";
import { useParams } from "react-router-dom";
import TooltipWarning from "../../../components/tooltip-warning";
import { queryLoaded } from "../../../utils/query.utils";
import { Events } from "../../../utils/constants";
import { useFilterContext } from "../../../contexts/filter";
import useUmamiTracking from "../../../hooks/use-umami-tracking";

const eventSlug: string = "talent-match";
const modalTalent = document.getElementById("modal-talent");

const CompanyTrackPage: React.FC = () => {
  useChat();
  const { isShown, toggle } = useModal();
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.authState.user);
  const { t } = useTranslation("private-home-page");
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const { track } = useParams<{ track: string }>();
  const { currentEventTrackData, currentTrackCandidatesData } = useSelector(
    (state: AppState) => state.eventState
  );
  const { currentTrackCandidatesProcessData } = useSelector(
    (state: AppState) => state.eventState
  );
  const devTableRef = useRef<HTMLDivElement>(null);
  const [isJobOfferOpen, setIsJobOfferOpen] = useState<boolean>(false);
  const { filters: localFilter } = useFilterContext();
  const pushUmami = useUmamiTracking();

  useEffect(() => {
    if (!currentTrackCandidatesData.loading) {
      dispatch(fetchSetInvisible());
    }
  }, [currentTrackCandidatesData.loading, dispatch]);

  const handleSearch = useCallback(
    (query) => {
      if (track) {
        dispatch(updateCurrentEventTrackData(track));
        dispatch(fetchTrackCandidates(track, query));
        dispatch(fetchTrackSkills(track));
        dispatch(fetchSetVisible());
        dispatch(fetchCandidateProcess(track));
        pushUmami(Events.SEARCH, {
          filters: localFilter,
          locale: 'track'
        }, user);
      }
    },
    [track, dispatch, localFilter, pushUmami, user]
  );

  useEffect(() => {
    dispatch(fetchEventData(eventSlug));
    dispatch(fetchEventTracks(eventSlug));
    dispatch(fetchCompanyPlan());
    dispatch(getCurrentPlan());
  }, [dispatch]);

  useEffect(() => {
    const loaded = queryLoaded(query);
    if (
      track &&
      !currentEventTrackData.data &&
      !currentEventTrackData.loading &&
      !currentEventTrackData.error &&
      loaded &&
      user
    ) {
      let q = { ...query };
      if (!q.currency) {
        q.currency = user?.corporate?.currency || "BRL";
      }
      setQuery(q);
      handleSearch(q);
    }
  }, [
    query,
    track,
    dispatch,
    handleSearch,
    currentEventTrackData,
    user,
    setQuery,
  ]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const keys = [...q.keys()];
    if (keys.length === 0 && user) {
      const currency = user?.corporate?.currency || "BRL";
      handleSearch({ currency });
      setQuery({ currency });
    }
  }, [track, user, handleSearch, setQuery]);

  useEffect(() => {
    return () => {
      dispatch(clearCurrentEventTrackData());
    };
  }, [dispatch]);

  const showCountdown = useMemo(() => {
    return !!currentEventTrackData.data?.end;
  }, [currentEventTrackData.data]);

  const title = useMemo(() => {
    if (currentEventTrackData.data?.origin_type === OriginType.ACCELERATION) {
      return t("tabs.trackContent.acceleration");
    }
    if (currentEventTrackData.data?.origin_type === OriginType.BOOTCAMP) {
      return t("tabs.trackContent.bootcamp");
    }
    return t("tabs.trackContent.vertical");
  }, [t, currentEventTrackData.data]);

  const subtitle = useMemo(() => {
    return (
      currentEventTrackData.data?.origin_name ||
      currentEventTrackData.data?.name
    );
  }, [currentEventTrackData.data]);

  const programProcessOptions = useMemo(() => {
    return [
      {
        value: "0-25",
        label: <FilterLabel>{t("quartile.first")}</FilterLabel>,
      },
      {
        value: "25-50",
        label: <FilterLabel>{t("quartile.second")}</FilterLabel>,
      },
      {
        value: "50-75",
        label: <FilterLabel>{t("quartile.third")}</FilterLabel>,
      },
      {
        value: "75-99",
        label: <FilterLabel>{t("quartile.fourth")}</FilterLabel>,
      },
      {
        value: "100",
        label: <FilterLabel>{t("quartile.completed")}</FilterLabel>,
      },
    ];
  }, [t]);

  const handlePageChange = useCallback(
    (page: number) => {
      query.trackingPage = page.toString();
      setQuery(query);

      if (devTableRef.current) {
        window.scrollTo({
          behavior: "smooth",
          top: devTableRef.current.offsetTop,
        });
      }
      handleSearch(query);
    },
    [query, setQuery, devTableRef, handleSearch]
  );

  const getOriginalWageClaim = useCallback(
    (candidate: Candidate) => {
      return candidate?.wage_claims?.[0]?.brl
        ? t("wage-claim-converted-original", {
            originalValue: parseFloat(
              candidate.wage_claims[0].brl
            ).toLocaleString(),
          })
        : t("wage-claim-converted");
    },
    [t]
  );

  const getQuartile = useCallback(
    (candidate: Candidate) => {
      if (candidate.has_completed) {
        return t("quartile.completed");
      } else if (candidate?.quartile) {
        if (!candidate.quartile.min && candidate.quartile.max === 25) {
          return t("quartile.first");
        } else if (
          candidate.quartile.min === 25 &&
          candidate.quartile.max === 50
        ) {
          return t("quartile.second");
        } else if (
          candidate.quartile.min === 50 &&
          candidate.quartile.max === 75
        ) {
          return t("quartile.third");
        } else if (candidate.quartile.min === 75 && !candidate.quartile.max) {
          return t("quartile.fourth");
        }
      }
    },
    [t]
  );

  const tableHeaderItems: HeaderItem[] = [
    { value: "position", width: "3%" },
    { value: "name", width: "10%" },
    { value: "main-skills", width: "25%" },
    { value: "experience", width: "15%" },
    { value: "wage-claim", width: "15%" },
  ];

  const tableHeaderItemsTechImpact: HeaderItem[] = [
    {
      value: "position",
      width: "3%",
      query_param: "orderBy",
      orderBy: [
        {
          value: "performance",
          label: (
            <FilterLabel>{t("ranking-table.filters.performance")}</FilterLabel>
          ),
        },
        {
          value: "resume",
          label: <FilterLabel>{t("ranking-table.filters.resume")}</FilterLabel>,
        },
      ],
    },
    ...tableHeaderItems.slice(1, tableHeaderItems.length),
    {
      value: "program-progress",
      width: "15%",
      query_param: "completed_range",
      filter: programProcessOptions,
    },
  ];

  const handleOpen = useCallback(() => {
    toggle();
  }, [toggle]);

  const handleClose = useCallback(() => {
    if (currentEventTrackData.data?.register) {
      // TODO: Se possível toda atualização vier pelo realtime também
      dispatch(fetchTrackCandidates(currentEventTrackData.data.uuid, query));
    }
    dispatch(clearCandidateDetail());
    toggle();
  }, [dispatch, toggle, currentEventTrackData.data, query]);

  const { candidateDetailData } = useSelector(
    (state: AppState) => state.candidateState
  );

  const [index, setIndex] = useState<number>(-1);

  const candidate = useMemo((): Candidate | undefined => {
    return index >= 0
      ? currentTrackCandidatesData.data?.results[index]
      : undefined;
  }, [currentTrackCandidatesData, index]);

  const candidateStatus = useCandidateStatus({
    candidateId: candidate?.id || "",
  });

  const handleModal = useCallback(
    (candidate: Candidate, candidateStatus) => {
      (!candidateStatus?.hired ||
        candidateStatus?.hired === user?.corporate.id ||
        candidate.company_hired === user?.corporate.id) &&
        handleOpen();
    },
    [handleOpen, user]
  ); 

  const showOptions: FilterOption[] = [
    {
      value: "suitable",
      action: () => {
        setQuery({ ...query, ranking_view: undefined });
        handleSearch({ ...query, ranking_view: undefined });
      },
    },
    {
      value: "not-suitable",
      action: () => {
        setQuery({ ...query, ranking_view: "not-suitable" });
        handleSearch({ ...query, ranking_view: "not-suitable" });
      },
    },
    {
      value: "all",
      action: () => {
        setQuery({ ...query, ranking_view: "all" });
        handleSearch({ ...query, ranking_view: "all" });
      },
    },
  ];

  const pageSizeOptions: FilterOption[] = [
    {
      value: "page-size-10",
      action: () => {
        setQuery({ ...query, pageSize: 10 });
        handleSearch({ ...query, pageSize: 10 });
      },
    },
    {
      value: "page-size-15",
      action: () => {
        setQuery({ ...query, pageSize: 15 });
        handleSearch({ ...query, pageSize: 15 });
      },
    },
    {
      value: "page-size-25",
      action: () => {
        setQuery({ ...query, pageSize: 25 });
        handleSearch({ ...query, pageSize: 25 });
      },
    },
  ];

  return (
    <CompanyTrackContainer>
      <Modal
        width={950}
        isShown={isShown}
        toggle={handleOpen}
        target={modalTalent}
        handleClose={handleClose}
        closeOnEsc={true}
        background={"#F3F3F3"}
      >
        <Slider
          onNext={() => setIndex(index + 1)}
          onPrev={() => setIndex(index - 1)}
          showNext={
            index < (currentTrackCandidatesData.data?.page_size || 0) - 1 &&
            !candidateDetailData.loading
          }
          showPrev={index !== 0 && !candidateDetailData.loading}
          disableKeyboard={isJobOfferOpen}
        >
          <Talent
            talentId={
              candidate
                ? candidate?.is_pool && candidate?.user_id
                  ? candidate?.user_id
                  : candidate?.id
                : ""
            }
            userId={candidate?.user_id || ""}
            trackId={candidate?.selection?.source_id}
            jobSetupId={currentEventTrackData.data?.job_setup}
            ecosystem={query.ecosystem}
            isNotifyOpenCandidateDetail={!candidate?.selection}
            currencyIso={query.currency}
            setIsJobOfferOpen={setIsJobOfferOpen}
          />
        </Slider>
      </Modal>
      <Container>
        <Header
          title={title}
          subtitle={subtitle}
          originType={currentEventTrackData.data?.origin_type}
          showCountdown={showCountdown}
          endDate={currentEventTrackData.data?.end}
          loading={currentEventTrackData.loading}
        />

        <Content>
          <TrackTitle>
            {currentEventTrackData.data?.origin_name ||
              currentEventTrackData.data?.name}
          </TrackTitle>
          <DashboardHeader>
            <ViewDesktopFilters>
              <FilterForm
                initialMaxSalary={50000}
                isTrack={true}
                grid
                reset={true}
                searchCallback={(query) => {
                  handleSearch({...query, trackingPage: '1'});
                }}
                disableKeyboard={isShown}
              />
            </ViewDesktopFilters>
          </DashboardHeader>
          {currentEventTrackData.data?.register && (
            <>
              <ContainerTable>
                <InfoTableContainer>
                  <TableCounter
                    count={currentTrackCandidatesData.data?.results.length || 0}
                    total={currentTrackCandidatesData.data?.count || 0}
                    inProcess={
                      currentTrackCandidatesProcessData.data?.total
                    }
                  />
                  <InfoTableContainer width="fit-content">
                    {!!currentEventTrackData.data?.job_setup && (
                      <TableFilter
                        label={"show"}
                        options={showOptions}
                        initial={showOptions.find(o => o.value === (query.ranking_view || "suitable"))}
                      />
                    )}
                    <PageSizeFilter pageSizeOptions={pageSizeOptions} />
                  </InfoTableContainer>
                </InfoTableContainer>
                <Table
                  headerItems={
                    currentEventTrackData.data?.is_techimpact
                      ? tableHeaderItemsTechImpact
                      : tableHeaderItems
                  }
                  handleSearch={handleSearch}
                >
                  {currentTrackCandidatesData.data?.results.map((c, key) => (
                    <TableRow
                      key={key}
                      onClick={() => {
                        setIndex(key);
                        handleModal(c, candidateStatus);
                      }}
                    >
                      <DataCell>
                        {getCandidatePosition(
                          currentTrackCandidatesData.data?.current_page,
                          currentTrackCandidatesData.data?.page_size,
                          key
                        )}
                        º
                      </DataCell>
                      <DataCell
                        color={theme.colors.primaryBlue.main}
                        bold={600}
                      >
                        {c.initials_name.replace(" ", "").toUpperCase()}
                      </DataCell>
                      <DataCell>{getMainSkills(c, t)}</DataCell>
                      <DataCell>{c.experience_time}</DataCell>
                      <DataCell>
                        {getCandidateSalary(c, t)}{" "}
                        {c.wage_claim_was_converted ? (
                          <TooltipWarning
                            message={getOriginalWageClaim(c)}
                            marginLeft=".5rem"
                          />
                        ) : (
                          <></>
                        )}
                      </DataCell>
                      {currentEventTrackData.data?.is_techimpact && (
                        <DataCell>{getQuartile(c)}</DataCell>
                      )}
                    </TableRow>
                  ))}
                </Table>
              </ContainerTable>
              {currentTrackCandidatesData.data &&
                currentTrackCandidatesData.data.results?.length > 0 && (
                  <Pagination
                    currentPage={currentTrackCandidatesData.data?.current_page}
                    pageNumbers={currentTrackCandidatesData.data?.total_pages}
                    onPageChange={handlePageChange}
                  />
                )}
            </>
          )}
        </Content>
      </Container>
    </CompanyTrackContainer>
  );
};

export default CompanyTrackPage;
